import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { format, parse, isValid, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getYear, setYear, setMonth } from 'date-fns';

const DatePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DateInput = styled.input<{ $hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#e2e8f0'};
  border-radius: 6px;
  font-size: 14px;
  background-color: ${props => props.$hasError ? '#fff5f5' : '#fff'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$hasError ? '#ef4444' : '#4A6CF7'};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : '#4A6CF7'};
    box-shadow: 0 0 0 3px ${props => props.$hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(74, 108, 247, 0.1)'};
  }
  
  &:disabled {
    background-color: #f1f5f9;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const CalendarDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1000;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  padding: 16px;
  min-width: 300px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  animation: slideDown 0.2s ease-out;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const MonthYearSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectWrapper = styled.select`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  background: white;
  cursor: pointer;
  color: #1e293b;
  
  &:hover {
    border-color: #4A6CF7;
  }
  
  &:focus {
    outline: none;
    border-color: #4A6CF7;
  }
`;

const NavButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: #64748b;
  
  &:hover {
    background: #4A6CF7;
    color: white;
  }
`;

const WeekdaysRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
`;

const Weekday = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 8px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`;

const DayCell = styled.button<{ 
  $isToday?: boolean; 
  $isSelected?: boolean; 
  $isCurrentMonth?: boolean;
  $isDisabled?: boolean;
}>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  font-weight: ${props => props.$isToday || props.$isSelected ? '600' : '400'};
  transition: all 0.15s;
  
  background: ${props => {
    if (props.$isSelected) return '#4A6CF7';
    if (props.$isToday) return '#e0e7ff';
    return 'transparent';
  }};
  
  color: ${props => {
    if (props.$isSelected) return 'white';
    if (!props.$isCurrentMonth) return '#cbd5e1';
    if (props.$isDisabled) return '#cbd5e1';
    return '#1e293b';
  }};
  
  opacity: ${props => props.$isDisabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background: ${props => props.$isSelected ? '#3b5de7' : '#f1f5f9'};
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background: ${props => props.$primary ? '#4A6CF7' : '#f1f5f9'};
  color: ${props => props.$primary ? 'white' : '#64748b'};
  
  &:hover {
    background: ${props => props.$primary ? '#3b5de7' : '#e2e8f0'};
  }
`;

interface ModernDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  name?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const ModernDatePicker: React.FC<ModernDatePickerProps> = ({
  value,
  onChange,
  disabled = false,
  hasError = false,
  placeholder = 'dd-mm-yyyy',
  minDate,
  maxDate,
  id,
  name
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => {
    if (value) {
      const parsed = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(parsed) ? parsed : new Date();
    }
    return new Date();
  });
  
  const wrapperRef = useRef<HTMLDivElement>(null);
  
  const selectedDate = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleDateClick = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    onChange(formattedDate);
    setIsOpen(false);
  };
  
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = parseInt(e.target.value);
    setViewDate(setMonth(viewDate, newMonth));
  };
  
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = parseInt(e.target.value);
    setViewDate(setYear(viewDate, newYear));
  };
  
  const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));
  
  const handleClear = () => {
    onChange('');
    setIsOpen(false);
  };
  
  const handleToday = () => {
    const today = new Date();
    const formattedDate = format(today, 'yyyy-MM-dd');
    onChange(formattedDate);
    setViewDate(today);
    setIsOpen(false);
  };
  
  const getDaysInMonth = () => {
    const start = startOfMonth(viewDate);
    const end = endOfMonth(viewDate);
    const days = eachDayOfInterval({ start, end });
    
    // Add padding days from previous month
    const startDay = start.getDay();
    const paddingDays = startDay === 0 ? 6 : startDay - 1; // Monday = 0
    
    const prevMonthEnd = new Date(start);
    prevMonthEnd.setDate(0);
    
    const paddedDays: Date[] = [];
    for (let i = paddingDays - 1; i >= 0; i--) {
      const day = new Date(prevMonthEnd);
      day.setDate(prevMonthEnd.getDate() - i);
      paddedDays.push(day);
    }
    
    // Add padding days for next month
    const endDay = end.getDay();
    const trailingDays = endDay === 0 ? 0 : 7 - endDay;
    
    const nextMonthDays: Date[] = [];
    for (let i = 1; i <= trailingDays; i++) {
      const day = new Date(end);
      day.setDate(end.getDate() + i);
      nextMonthDays.push(day);
    }
    
    return [...paddedDays, ...days, ...nextMonthDays];
  };
  
  const isDateDisabled = (date: Date) => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };
  
  const displayValue = selectedDate && isValid(selectedDate) 
    ? format(selectedDate, 'dd-MM-yyyy') 
    : '';
  
  // Generate year options (100 years back from current year)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 101 }, (_, i) => currentYear - 100 + i);
  
  return (
    <DatePickerWrapper ref={wrapperRef}>
      <DateInput
        type="text"
        value={displayValue}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        readOnly
        disabled={disabled}
        $hasError={hasError}
        placeholder={placeholder}
        id={id}
        name={name}
      />
      
      <CalendarDropdown $isOpen={isOpen}>
        <CalendarHeader>
          <NavButton onClick={handlePrevMonth} type="button">
            ←
          </NavButton>
          
          <MonthYearSelector>
            <SelectWrapper value={viewDate.getMonth()} onChange={handleMonthChange}>
              {MONTHS.map((month, index) => (
                <option key={month} value={index}>{month}</option>
              ))}
            </SelectWrapper>
            
            <SelectWrapper value={getYear(viewDate)} onChange={handleYearChange}>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </SelectWrapper>
          </MonthYearSelector>
          
          <NavButton onClick={handleNextMonth} type="button">
            →
          </NavButton>
        </CalendarHeader>
        
        <WeekdaysRow>
          {WEEKDAYS.map(day => (
            <Weekday key={day}>{day}</Weekday>
          ))}
        </WeekdaysRow>
        
        <DaysGrid>
          {getDaysInMonth().map((date, index) => {
            const isCurrentMonth = isSameMonth(date, viewDate);
            const isToday = isSameDay(date, new Date());
            const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
            const isDisabled = isDateDisabled(date);
            
            return (
              <DayCell
                key={index}
                type="button"
                onClick={() => !isDisabled && handleDateClick(date)}
                $isToday={isToday}
                $isSelected={isSelected}
                $isCurrentMonth={isCurrentMonth}
                $isDisabled={isDisabled}
                disabled={isDisabled}
              >
                {date.getDate()}
              </DayCell>
            );
          })}
        </DaysGrid>
        
        <FooterActions>
          <ActionButton type="button" onClick={handleClear}>
            Clear
          </ActionButton>
          <ActionButton type="button" $primary onClick={handleToday}>
            Today
          </ActionButton>
        </FooterActions>
      </CalendarDropdown>
    </DatePickerWrapper>
  );
};

export default ModernDatePicker;
